import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { mediaFactsLine } from '../composerMediaText';
import { pickerMessages } from './pickerMessages';
import { pickerAspectLabel } from './pickerFiles';
import { uploadPickerKey } from './pickerUploads';
import { PickerTile } from './PickerTile';
import { ComposerMediaPicker } from './useComposerMediaPicker';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './ComposerMediaPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PickerUploadsSectionProps {
  picker: ComposerMediaPicker;
}

/**
 * "Your uploads" (design assets-rethink a1, decision 8): the photos and videos that the artist
 * brought before, for a new post. There is no Library page; the picker and the "All files" search
 * of Releases list them. Nothing shows while the artist has no upload.
 */
const PickerUploadsSectionComponent: React.FC<PickerUploadsSectionProps> = ({ picker }) => {
  const intl = useIntl();
  const { uploads, selectedKey, select, attachKey } = picker;
  if (uploads.length === 0) {
    return null;
  }
  return (
    <section data-testid="picker-uploads">
      <span className={styles.sectionName}>{intl.formatMessage(pickerMessages.yourUploads)}</span>
      <ul className={styles.tileList}>
        {uploads.map((upload) => {
          const key = uploadPickerKey(upload);
          return (
            <PickerTile
              key={key}
              tileKey={key}
              name={upload.name ?? intl.formatMessage(pickerMessages.uploadNoName)}
              facts={mediaFactsLine(intl, {
                kind: upload.mediaKind,
                aspect: pickerAspectLabel(upload),
                durationSec: upload.mediaKind === 'video' ? upload.durationSec : null,
              })}
              kind={upload.mediaKind}
              pictureUrl={upload.mediaKind === 'image' ? getFullAPIFileURL(upload.url) : null}
              isSelected={selectedKey === key}
              onSelect={select}
              onAttach={attachKey}
            />
          );
        })}
      </ul>
    </section>
  );
};

export const PickerUploadsSection = TypedMemo(PickerUploadsSectionComponent);
